import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Save, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function AdminLlmsTxt() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/llms").then(({ data }) => setContent(data.content)).catch(() => toast.error("Failed to load llms.txt"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/llms", { content });
      toast.success("Saved. Live now at /api/llms.txt — redeploy to update /llms.txt on your live site.");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset llms.txt back to the default content?")) return;
    try {
      const { data } = await api.post("/admin/llms/reset");
      setContent(data.content);
      toast.success("Reverted to default.");
    } catch { toast.error("Reset failed"); }
  };

  if (content === null) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 md:p-10 space-y-5 max-w-4xl" data-testid="admin-llms-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">AI Search</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight flex items-center gap-2"><FileText className="h-6 w-6 text-[#FF331F]" /> llms.txt</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">Plain-text summary that AI answer engines (Perplexity, ChatGPT, etc.) read to describe and recommend your business. Edits go live instantly at <span className="text-zinc-300">/api/llms.txt</span>; the root <span className="text-zinc-300">/llms.txt</span> updates on your next redeploy.</p>
        </div>
        <div className="flex gap-2">
          <a href="/api/llms.txt" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white border border-[#27272A] px-3 py-2 rounded-sm"><ExternalLink className="h-3.5 w-3.5" /> View live</a>
          <Button onClick={reset} variant="outline" className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs uppercase tracking-widest" data-testid="llms-reset"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold" data-testid="llms-save"><Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}</Button>
        </div>
      </header>

      <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={30}
        data-testid="llms-textarea"
        className="bg-black border-[#27272A] text-zinc-100 rounded-sm font-mono text-sm leading-relaxed" />
    </div>
  );
}
